import React, { useEffect, useState } from 'react'
import { FaExclamationTriangle, FaArchive, FaTrash, FaSpinner, FaCheck } from 'react-icons/fa'
import { Modal, Banner } from './ui'
import { number } from '../lib/format'
import { STATUS } from '../lib/viz'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * Remove a product — by archiving it, or by actually deleting it.
 *
 * Deliberately not a bare "are you sure?". That question is unanswerable without
 * knowing what the product is attached to, and the honest answer differs:
 *
 *   never sold      deleting is clean, nothing is lost
 *   has sold        deleting keeps the order lines (they snapshot their own
 *                   name, sku and price) but NULLs their link to the product,
 *                   so it vanishes from sales reporting while the money stays
 *                   real. Archiving is almost always what was meant.
 *
 * So the dialog loads the usage first and recommends accordingly. Archive is the
 * default action; permanent deletion requires typing the product name, which is
 * friction on purpose — it is the one action here with no undo.
 */
const DeleteProductDialog = ({ product, open, onClose, onDone }) => {
  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [confirmText, setConfirmText] = useState('')

  useEffect(() => {
    if (!open || !product) return
    setLoading(true)
    setError(null)
    setConfirmText('')
    api
      .getProductUsage(product.id)
      .then(setUsage)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [open, product?.id])

  const hasSold = (usage?.orderLines ?? 0) > 0
  const nameMatches = confirmText.trim().toLowerCase() === (product?.name ?? '').trim().toLowerCase()

  const run = async (action) => {
    setBusy(true)
    setError(null)
    try {
      if (action === 'archive') {
        await api.archiveProduct(product.id)
        onDone(`${product.name} archived. It is off the shop but every record is intact.`)
      } else {
        await api.deleteProduct(product.id)
        onDone(`${product.name} deleted permanently.`)
      }
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (!product) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Remove ${product.name}`}
      footer={<button onClick={onClose} className="btn-ghost">Cancel</button>}
    >
      {loading ? (
        <p className="py-6 text-center text-2xs text-ink-muted">
          <FaSpinner className="mr-2 inline animate-spin" />
          Checking what this product is attached to…
        </p>
      ) : (
        <div className="space-y-4">
          {error && <Banner tone="error">{error}</Banner>}

          {/* What is actually at stake, before any choice is offered. */}
          <div className="rounded-lg border border-hairline bg-plane/60 p-3.5">
            <div className={`grid gap-3 text-center ${SHOW_STOCK ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <Fact label="Orders" value={number(usage?.orderLines ?? 0)} warn={hasSold} />
              <Fact label="Units sold" value={number(usage?.unitsSold ?? 0)} warn={hasSold} />
              {SHOW_STOCK && <Fact label="Stock lines" value={number(usage?.stockLines ?? 0)} />}
            </div>
          </div>

          {hasSold ? (
            <Banner tone="warning">
              <strong>This product has been sold.</strong> Deleting it keeps those order lines —
              they carry their own copy of the name and price — but breaks their link to this
              product, so it disappears from your sales reports even though the money was real.
              Archive it instead.
            </Banner>
          ) : (
            <Banner tone="info">
              This product has never been ordered, so deleting it removes it cleanly.
            </Banner>
          )}

          {/* ---- Archive: the recommended path ---- */}
          <div className="rounded-lg border border-hairline p-3.5">
            <div className="flex items-start gap-2.5">
              <FaArchive className="mt-0.5 shrink-0 text-[13px] text-ink-muted" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h3 className="text-[13px] font-semibold text-ink">
                  Archive it {hasSold && <span className="text-status-good-text">— recommended</span>}
                </h3>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  Comes off the shop immediately and cannot be ordered. Every past order and sales
                  figure stays exactly as it is. You can put it back any time.
                </p>
                <button
                  onClick={() => run('archive')}
                  disabled={busy}
                  className="btn-primary btn-sm mt-2.5"
                >
                  {busy ? <FaSpinner className="animate-spin" /> : <FaArchive className="text-[10px]" />}
                  Archive this product
                </button>
              </div>
            </div>
          </div>

          {/* ---- Delete: gated behind typing the name ---- */}
          <div className="rounded-lg border p-3.5" style={{ borderColor: `${STATUS.critical}40` }}>
            <div className="flex items-start gap-2.5">
              <FaExclamationTriangle
                className="mt-0.5 shrink-0 text-[13px]"
                style={{ color: STATUS.critical }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-[13px] font-semibold text-ink">Delete permanently</h3>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  Removes the product{SHOW_STOCK
                    ? `, its ${number(usage?.stockLines ?? 0)} stock line${usage?.stockLines === 1 ? '' : 's'} and its category links`
                    : ' and its category links'}. This cannot be undone. Uploaded photographs are
                  left in storage.
                </p>

                <label className="label mt-2.5" htmlFor="confirm">
                  Type <span className="font-semibold text-ink">{product.name}</span> to confirm
                </label>
                <input
                  id="confirm"
                  className="input input-sm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={product.name}
                  autoComplete="off"
                />

                <button
                  onClick={() => run('delete')}
                  disabled={busy || !nameMatches}
                  className="btn-danger btn-sm mt-2.5"
                >
                  {busy ? <FaSpinner className="animate-spin" /> : <FaTrash className="text-[10px]" />}
                  Delete permanently
                </button>
                {nameMatches && !busy && (
                  <span className="ml-2 text-2xs text-ink-muted">
                    <FaCheck className="mr-1 inline text-[8px]" style={{ color: STATUS.good }} />
                    name matches
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

const Fact = ({ label, value, warn }) => (
  <div>
    <div
      className="text-lg font-semibold tabular-nums"
      style={warn ? { color: STATUS.warning } : undefined}
    >
      {value}
    </div>
    <div className="text-2xs uppercase tracking-[0.08em] text-ink-muted">{label}</div>
  </div>
)

export default DeleteProductDialog
